import { AxiosResponse } from 'axios';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import {
  IntegrationField,
  JiraApi,
  JiraConfig,
  JiraEpic,
  JiraIssue,
  JiraissueLinkProps,
  JiraIssueType,
  JiraProject,
} from 'api/integrations';
import { UserType } from 'api/models';
import { LinkedResource } from 'api/tasks';
import { useUserType } from 'hooks/use-user-type';
import { fiveMinutes } from './constants';

const defaultOptions = {
  retry: 1,
  refetchOnWindowFocus: false,
  staleTime: fiveMinutes,
};

export const GET_JIRA_CONFIG = 'getJiraConfig';

export const useQueryJiraConfig = (
  options: UseQueryOptions<
    JiraConfig[],
    unknown,
    JiraConfig[],
    typeof GET_JIRA_CONFIG
  > = {},
): UseQueryResult<JiraConfig[], unknown> => {
  const userType = useUserType();

  return useQuery(GET_JIRA_CONFIG, () => JiraApi.getJiraIntegration(), {
    ...defaultOptions,
    ...options,
    enabled: userType === UserType.Native,
  });
};

export const GET_JIRA_ISSUE = 'getJiraIssue';

type GetJiraIssueQueryKey = [typeof GET_JIRA_ISSUE, number, string];
export const useGetJiraIssue = (
  integrationId: number,
  issueId: string,
  options: UseQueryOptions<
    JiraIssue,
    unknown,
    JiraIssue,
    GetJiraIssueQueryKey
  > = {},
): UseQueryResult<JiraIssue, unknown> => {
  return useQuery(
    [
      GET_JIRA_ISSUE,
      integrationId,
      issueId,
    ] /* TODO:: @krishna queryKey can be moved to separate func */,
    () => JiraApi.getJiraIssue(integrationId, issueId),
    {
      ...defaultOptions,
      ...options,
    },
  );
};

/* TODO:: @krishna typo */
export const GET_JIRA_FIELDS = 'getJiraProjects';
export const useQueryGetJiraFields = (
  integrationId: number,
  projectId: string,
  issueTypeId: string,
  options: UseQueryOptions<
    IntegrationField[],
    unknown,
    IntegrationField[],
    [typeof GET_JIRA_FIELDS, number, string, string]
  > = {},
): UseQueryResult<IntegrationField[], unknown> => {
  return useQuery(
    [GET_JIRA_FIELDS, integrationId, projectId, issueTypeId],
    () => JiraApi.getJiraIssueFields(integrationId, projectId, issueTypeId),
    {
      ...defaultOptions,
      ...options,
    },
  );
};

/* TODO:: @krishna typo */
export const GET_JIRA_PROJECTS = 'getJiraFields';

export const useQueryGetJiraProjects = (
  integrationId: number,
  options: UseQueryOptions<
    JiraProject[],
    unknown,
    JiraProject[],
    [typeof GET_JIRA_PROJECTS, number]
  > = {},
): UseQueryResult<JiraProject[], unknown> => {
  return useQuery(
    [GET_JIRA_PROJECTS, integrationId],
    () => JiraApi.getJiraProjects(integrationId),
    {
      ...defaultOptions,
      ...options,
    },
  );
};

export const GET_JIRA_ISSUETYPE_OPTIONS = 'getJiraIssueTypeOptions';

export const useQueryJiraIssueTypeOptions = (
  integrationId: number,
  projectId: string,
  options: UseQueryOptions<
    JiraIssueType[],
    unknown,
    JiraIssueType[],
    [typeof GET_JIRA_ISSUETYPE_OPTIONS, number, string]
  > = {},
): UseQueryResult<JiraIssueType[], unknown> => {
  return useQuery(
    [GET_JIRA_ISSUETYPE_OPTIONS, integrationId, projectId],
    () => JiraApi.getIssueTypeOptions(integrationId, projectId),
    {
      ...defaultOptions,
      ...options,
    },
  );
};

export const GET_JIRA_PROJECT_ISSUETYPE = 'getJiraProjectIssueType';

export const useQueryJiraIssueTypes = (
  integrationId: number,
  projectId: string,
  options: UseQueryOptions<
    JiraIssueType[],
    unknown,
    JiraIssueType[],
    [typeof GET_JIRA_PROJECT_ISSUETYPE, number, string]
  > = {},
): UseQueryResult<JiraIssueType[], unknown> => {
  return useQuery(
    [GET_JIRA_PROJECT_ISSUETYPE, integrationId, projectId],
    () => JiraApi.getJiraProjectIssueType(integrationId, projectId),
    {
      ...defaultOptions,
      ...options,
    },
  );
};

export const GET_JIRA_PROJECT_EPIC = 'getJiraProjectEpic';

export const useQueryJiraEpic = (
  integrationId: number,
  projectId: string,
  options: UseQueryOptions<
    JiraEpic[],
    unknown,
    JiraEpic[],
    [typeof GET_JIRA_PROJECT_EPIC, number, string]
  > = {},
): UseQueryResult<JiraEpic[], unknown> => {
  return useQuery(
    [GET_JIRA_PROJECT_EPIC, integrationId, projectId],
    () => JiraApi.getJiraProjectEpics(integrationId, projectId),
    {
      ...defaultOptions,
      ...options,
    },
  );
};

export const useQueryJiraRangeEpic = (
  integrationId: number,
  projectId: string,
  options: UseQueryOptions<
    AxiosResponse<JiraEpic[]>,
    unknown,
    AxiosResponse<JiraEpic[]>,
    [typeof GET_JIRA_PROJECT_EPIC, number, string]
  > = {},
): UseQueryResult<AxiosResponse<JiraEpic[]>, unknown> => {
  return useQuery(
    [GET_JIRA_PROJECT_EPIC, integrationId, projectId],
    () => JiraApi.getJiraProjectRangeEpics(integrationId, projectId),
    {
      ...defaultOptions,
      ...options,
    },
  );
};

export const useJiraIssueLinkMutation = (
  integrationid: number,
  options: UseMutationOptions<LinkedResource, unknown, JiraissueLinkProps> = {},
): UseMutationResult<LinkedResource, unknown, JiraissueLinkProps> =>
  useMutation<LinkedResource, unknown, JiraissueLinkProps>(
    (payload) => JiraApi.linkJiraIssueToTask(integrationid, payload),
    options,
  );

export const useJiraIssueUnlinkMutation = (
  integrationid: number,
  options: UseMutationOptions<number, unknown, number> = {},
): UseMutationResult<number, unknown, number> =>
  useMutation<number, unknown, number>(
    (resourceMappingId: number) =>
      JiraApi.unlinkJiraIssueFromTask(integrationid, resourceMappingId),
    options,
  );

export const useJiraIssueDeleteMutation = (
  integrationid: number,
  options: UseMutationOptions<number, unknown, string> = {},
): UseMutationResult<number, unknown, string> =>
  useMutation<number, unknown, string>(
    (issueId: string) => JiraApi.deleteJiraIssue(integrationid, issueId),
    options,
  );
